// All variables are globally available from subscription-data.js
// No imports needed since scripts are loaded in the Liquid template

class SubscriptionBuilder {
  constructor() {
    this.currentStep = 1;
    this.selection = {
      product: null,
      quantity: null,
      frequency: null
    };
    this.maxStep = 1;
    this.loadPersistedState();
    this.init();
  }

  getStrings() {
    const strings = window.SUBSCRIPTION_BUILDER_STRINGS || {};
    const variantStrings = window.variantStrings || {};

    return {
      select: strings.select || 'Select',
      selected: strings.selected || 'Selected',
      soldOut: strings.soldOut || variantStrings.soldOut || 'Sold out',
      addToCart: strings.addToCart || variantStrings.addToCart || 'Add to cart'
    };
  }

  getStorageKey() {
    return `subscription-builder:${window.location.pathname}`;
  }

  loadPersistedState() {
    try {
      const raw = sessionStorage.getItem(this.getStorageKey());
      if (!raw) return;
      const data = JSON.parse(raw);
      const selection = {
        product: null,
        quantity: null,
        frequency: null
      };

      const productHandle = data?.productHandle;
      const product = productHandle ? META[productHandle] : null;
      if (product && product.available !== false) {
        selection.product = {
          handle: productHandle,
          ...product,
          url: `/products/${productHandle}`
        };

        const quantity = data?.quantity;
        const quantityData = quantity ? selection.product.quantities?.[quantity] : null;
        if (quantityData && quantityData.available !== false) {
          selection.quantity = quantity;
          const frequency = data?.frequency;
          if (frequency && FREQUENCIES.includes(frequency) && selection.product.plans?.[frequency]) {
            selection.frequency = frequency;
          }
        }
      }

      this.selection = selection;

      const maxStep = 1 +
        (selection.product ? 1 : 0) +
        (selection.quantity ? 1 : 0) +
        (selection.frequency ? 1 : 0);

      this.maxStep = Math.max(1, maxStep);

      let currentStep = Number(data?.currentStep);
      if (!Number.isFinite(currentStep)) {
        currentStep = this.maxStep;
      }
      if (currentStep < 1) currentStep = 1;
      if (currentStep > this.maxStep) currentStep = this.maxStep;
      this.currentStep = currentStep;
    } catch (error) {
      console.warn('Failed to load subscription builder state', error);
    }
  }

  saveState() {
    try {
      const data = {
        productHandle: this.selection.product?.handle || null,
        quantity: this.selection.quantity || null,
        frequency: this.selection.frequency || null,
        currentStep: this.currentStep
      };
      sessionStorage.setItem(this.getStorageKey(), JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save subscription builder state', error);
    }
  }

  init() {
    this.renderCategories();
    if (this.selection.product?.category) {
      this.switchCategory(this.selection.product.category);
    }
    this.bindEvents();
    this.bindStepNavigation();
    this.updateSteps();
    if (this.currentStep > 1) {
      this.renderCurrentStep();
    }
  }

  renderCategories() {
    const categoriesContainer = document.querySelector('.categories-tabs');
    const productsContainer = document.querySelector('.products-container');
    const categorySelect = document.querySelector('.category-select');
    const strings = this.getStrings();
    const allCategories = Object.keys(CATEGORIES);
    const availableCategories = allCategories.filter((category) => CATEGORIES[category]?.length);
    const categories = availableCategories.length ? availableCategories : allCategories;

    // Render category tabs
    categoriesContainer.innerHTML = categories.map((category, index) => `
      <button 
        class="category-tab${index === 0 ? ' active' : ''}" 
        data-category="${category}"
        role="tab"
        aria-selected="${index === 0}"
        tabindex="${index === 0 ? '0' : '-1'}"
      >
        ${category}
      </button>
    `).join('');

    if (categorySelect) {
      categorySelect.innerHTML = categories.map((category, index) => `
        <option value="${category}" ${index === 0 ? 'selected' : ''}>${category}</option>
      `).join('');
    }

    // Update product card template to include a select button
    productsContainer.innerHTML = categories.map((category, index) => {
      const products = CATEGORIES[category] || [];
      // Sort alphabetically by product name for all categories except Popular
      let sortedProducts = [...products];
      if (category !== 'Popular') {
        sortedProducts.sort((a, b) => {
          const nameA = META[a]?.name?.toLowerCase() || '';
          const nameB = META[b]?.name?.toLowerCase() || '';
          return nameA.localeCompare(nameB);
        });
      }

      return `
      <div class="category-products${index === 0 ? ' active' : ''}" data-category="${category}">
        <div class="products-grid row center-lg">
          ${sortedProducts.map(handle => {
        const product = META[handle];
        const isAvailable = product.available !== false;
        const isSelected = isAvailable && this.selection.product?.handle === handle;
        const buttonLabel = isAvailable ? (isSelected ? strings.selected : strings.select) : strings.soldOut;
        const buttonAriaLabel = isAvailable
          ? `${isSelected ? strings.selected : strings.select} ${product.name}`
          : `${product.name} ${strings.soldOut}`;
        return `
              <div class="product-wrapper col-lg-3 col-md-3 col-sm-4 col-xs-6 col-xxs-6">
                <div class="product-card" data-product-handle="${handle}">
                  <img 
                    src="${product.featured_image}"
                    alt="${product.name}"
                    loading="lazy"
                    data-image-type="${product.imageType || 'bag'}"
                  >
                  <h3>${product.name}</h3>
                  ${product.product_flavor ? `<p class="product-flavor">${product.product_flavor}</p>` : ''}
                  <p>${product.price}</p>
                  <button 
                    class="select-product-btn${isSelected ? ' selected' : ''}${isAvailable ? '' : ' sold-out'}" 
                    data-handle="${handle}"
                    aria-label="${buttonAriaLabel}"
                    ${isAvailable ? '' : 'disabled aria-disabled="true"'}
                  >
                    ${buttonLabel}
                  </button>
                </div>
              </div>
            `;
      }).join('')}
        </div>
      </div>
    `;
    }).join('');
  }

  bindEvents() {
    // Category switching - using event delegation for tabs
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('category-tab')) {
        const category = e.target.dataset.category;
        this.switchCategory(category);
      }
    });

    // Category switching - mobile dropdown
    const categorySelect = document.querySelector('.category-select');
    if (categorySelect) {
      categorySelect.addEventListener('change', (e) => {
        this.switchCategory(e.target.value);
      });
    }

    // Product selection through select button
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('select-product-btn')) {
        const handle = e.target.dataset.handle;
        this.handleProductSelect(handle);
      }
    });
  }

  switchCategory(category) {
    if (!category) return;

    // Update active tab and ARIA attributes
    document.querySelectorAll('.category-tab').forEach(tab => {
      const isActive = tab.dataset.category === category;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', isActive);
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    // Update mobile dropdown
    const categorySelect = document.querySelector('.category-select');
    if (categorySelect && categorySelect.value !== category) {
      categorySelect.value = category;
    }

    // Show selected category products
    document.querySelectorAll('.category-products').forEach(products => {
      products.classList.toggle('active', products.dataset.category === category);
    });
  }

  handleProductSelect(handle) {
    if (!handle || !META[handle]) {
      this.showError('Invalid product selection');
      return;
    }

    const strings = this.getStrings();

    // If clicking the same product, just advance to next step
    if (this.selection.product?.handle === handle) {
      this.renderQuantities();
      this.nextStep();
      return;
    }

    // Selecting a different product - reset subsequent selections
    const product = META[handle];

    // Reset selections for subsequent steps
    this.selection = {
      product: {
        handle,
        ...product,
        // Ensure we're using the correct URL format
        url: `/products/${handle}`
      },
      quantity: null,
      frequency: null
    };

    // Reset step progress when switching products
    this.currentStep = 1;
    this.maxStep = 1;

    // Reset UI selections for subsequent steps
    document.querySelectorAll('.quantity-card .selected, .frequency-card .selected').forEach(btn => {
      btn.classList.remove('selected');
      if (btn.textContent === strings.selected) {
        btn.textContent = strings.select;
      }
    });

    // Clear summary and quick-add form
    const summaryContent = document.querySelector('.summary-content');
    if (summaryContent) {
      summaryContent.innerHTML = '';
    }

    const quickAdd = document.querySelector('.quick-add');
    if (quickAdd) {
      quickAdd.innerHTML = '';
    }

    // Update product selection UI
    document.querySelectorAll('.select-product-btn').forEach(btn => {
      if (btn.disabled) return;
      const isSelected = btn.dataset.handle === handle;
      btn.classList.toggle('selected', isSelected);
      btn.textContent = isSelected ? strings.selected : strings.select;
    });

    // Re-render quantities with reset state
    this.renderQuantities();
    this.nextStep();
  }

  renderQuantities() {
    const quantitiesGrid = document.querySelector('.quantities-grid');
    const stepHeader = document.querySelector('#step-content-2 .step-header');
    if (!this.selection.product) return;
    const strings = this.getStrings();

    // Update step header based on product type
    if (stepHeader && window.STEP_TEXTS?.step2) {
      const imageType = this.selection.product.imageType || 'bag';
      const texts = window.STEP_TEXTS.step2[imageType] || window.STEP_TEXTS.step2.bag;

      stepHeader.innerHTML = `
        ${texts.title ? `<h2 class="step-title">${texts.title}</h2>` : ''}
        ${texts.subtext ? `<p class="step-subtext">${texts.subtext}</p>` : ''}
      `;
    }

    const quantities = Object.entries(this.selection.product.quantities);

    quantitiesGrid.innerHTML = `<div class="row center-lg">${quantities.map(([size, data]) => {
      const isSelected = this.selection.quantity === size;
      const assetUrl = getQuantityAsset(size, this.selection.product.imageType);
      const isAvailable = data.available !== false;
      const buttonLabel = isAvailable ? (isSelected ? strings.selected : strings.select) : strings.soldOut;
      const buttonAriaLabel = isAvailable
        ? `${isSelected ? strings.selected : strings.select} ${size} quantity`
        : `${size} quantity ${strings.soldOut}`;

      return `
        <div class="col-lg-3 col-md-3 col-sm-4 col-xs-6 col-xxs-6 product-wrapper">
          <div class="quantity-card" data-quantity="${size}">
            ${assetUrl ? `<img src="${assetUrl}" alt="${size}">` : ''}
            <h3>${size}</h3>
            <p>$${data.price}</p>
            <button 
              class="select-quantity-btn${isSelected ? ' selected' : ''}${isAvailable ? '' : ' sold-out'}" 
              data-quantity="${size}"
              aria-label="${buttonAriaLabel}"
              ${isAvailable ? '' : 'disabled aria-disabled="true"'}
            >
              ${buttonLabel}
            </button>
          </div>
        </div>
      `;
    }).join('')}</div>`;

    // Bind quantity selection events
    quantitiesGrid.querySelectorAll('.select-quantity-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const quantity = btn.dataset.quantity;
        this.handleQuantitySelect(quantity);
      });
    });
  }

  handleQuantitySelect(quantity) {
    if (!this.selection.product || !quantity) {
      this.showError('Please select a product first');
      return;
    }

    const strings = this.getStrings();

    this.selection.quantity = quantity;

    // Update UI to show selection
    document.querySelectorAll('.select-quantity-btn').forEach(btn => {
      if (btn.disabled) {
        btn.classList.remove('selected');
        btn.textContent = strings.soldOut;
        return;
      }
      const isSelected = btn.dataset.quantity === quantity;
      btn.classList.toggle('selected', isSelected);
      btn.textContent = isSelected ? strings.selected : strings.select;
    });

    this.renderFrequencies();
    this.nextStep();
  }

  renderFrequencies() {
    const frequencyOptions = document.querySelector('.frequency-options');
    if (!this.selection.product) return;
    const strings = this.getStrings();

    frequencyOptions.innerHTML = `<div class="row center-lg">${FREQUENCIES.map(frequency => {
      const isSelected = this.selection.frequency === frequency;
      const assetUrl = FREQUENCY_ASSETS[frequency];
      const buttonLabel = isSelected ? strings.selected : strings.select;
      const buttonAriaLabel = `${isSelected ? strings.selected : strings.select} ${frequency} delivery frequency`;

      return `
        <div class="col-lg-3 col-md-3 col-sm-4 col-xs-6 col-xxs-6 product-wrapper">
          <div class="frequency-card" data-frequency="${frequency}">
            ${assetUrl ? `<img src="${assetUrl}" alt="${frequency}">` : ''}
            <h3>${frequency}</h3>
            <button 
              class="select-frequency-btn${isSelected ? ' selected' : ''}" 
              data-frequency="${frequency}"
              aria-label="${buttonAriaLabel}"
            >
              ${buttonLabel}
            </button>
          </div>
        </div>
      `;
    }).join('')}</div>`;

    // Bind frequency selection events
    frequencyOptions.querySelectorAll('.select-frequency-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const frequency = btn.dataset.frequency;
        this.handleFrequencySelect(frequency);
      });
    });
  }

  handleFrequencySelect(frequency) {
    if (!this.selection.product || !this.selection.quantity) {
      this.showError('Please complete previous steps first');
      return;
    }

    const strings = this.getStrings();

    this.selection.frequency = frequency;

    // Update UI to show selection
    document.querySelectorAll('.select-frequency-btn').forEach(btn => {
      const isSelected = btn.dataset.frequency === frequency;
      btn.classList.toggle('selected', isSelected);
      btn.textContent = isSelected ? strings.selected : strings.select;
    });

    this.nextStep();
    this.renderSummary();
  }

  renderSummary() {
    if (!this.selection.product || !this.selection.quantity || !this.selection.frequency) {
      this.showError('Please complete all previous steps first');
      return;
    }

    const strings = this.getStrings();
    const summaryContent = document.querySelector('.summary-content');
    const product = this.selection.product;
    const quantityData = product.quantities[this.selection.quantity];

    if (!quantityData) {
      this.showError('Invalid quantity selection');
      return;
    }

    // Get frequency interval for display
    const frequencyInterval = FREQUENCY_INTERVALS[this.selection.frequency] || 2;
    const frequencyText = frequencyInterval === 1 ? 'Every Week' : `Every ${frequencyInterval === 2 ? 'Two' : frequencyInterval === 4 ? 'Four' : frequencyInterval} Weeks`;

    // Build product type description
    let productTypeDesc = '';
    switch (product.imageType) {
      case 'sachet':
        productTypeDesc = `Té Sachet – ${product.name} – ${this.selection.quantity}`;
        break;
      case 'cookiebox':
        productTypeDesc = `${product.name} – ${this.selection.quantity}`;
        break;
      default:
        productTypeDesc = `${product.name} – ${this.selection.quantity}`;
    }

    // Clear the quick-add container
    const quickAdd = document.querySelector('.quick-add');
    quickAdd.innerHTML = '';

    // Render product card in summary-content (constrained to 250px)
    summaryContent.innerHTML = `
      <div class="product-card">
        <img src="${product.featured_image}" alt="${product.name}" loading="lazy">
        <h3>${product.name}</h3>
        <p class="product-flavor">${this.selection.quantity} · ${frequencyText}</p>
        <p>$${quantityData.price}</p>
        <product-form class="product-form">
          <form method="post" action="/cart/add" id="quick-add-form-${product.handle}" accept-charset="UTF-8" class="form" enctype="multipart/form-data" novalidate="novalidate">
            <input type="hidden" name="form_type" value="product">
            <input type="hidden" name="utf8" value="✓">
            <input type="hidden" name="id" value="${quantityData.id}">
            <input type="hidden" name="selling_plan" value="${product.plans[this.selection.frequency]}">
            <div class="product-form__buttons">
              <button
                type="submit"
                name="add"
                class="product-form__submit button button--full-width button--primary"
                aria-haspopup="dialog"
              >
                <span>${strings.addToCart}</span>
                <div class="loading__spinner hidden">
                  <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66">
                    <circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle>
                  </svg>
                </div>
              </button>
            </div>
          </form>
        </product-form>
      </div>
    `;

    // Render fancy-rule and summary in quick-add
    quickAdd.innerHTML = `
      <div class="t-fancy-rule">
        <div class="t-fancy-rule-left"> </div>
        <img alt="" role="presentation" src="https://tecompanytea.com/cdn/shop/t/75/assets/fancy-rule.svg?v=108445150395769692611727303395">
        <div class="t-fancy-rule-right"> </div>
      </div>
      
      <div class="t-subscription-review-summary">
        <p>
          Your <span class="t-review-product">${productTypeDesc}</span> will be delivered to you <span class="t-review-frequency">${frequencyText}</span>. 
          You will be charged <span class="t-review-frequency">$${quantityData.price} ${frequencyText}</span>. 
          You can modify or cancel your subscription at any time.
        </p>
        <p>Shipping &amp; taxes calculated at checkout.</p>
      </div>
    `;

    const form = summaryContent.querySelector('form');
    const handleSubmit = async (e) => {
      e.preventDefault();

      const submitButton = form.querySelector('button[name="add"]');
      submitButton.classList.add('loading');
      submitButton.querySelector('.loading__spinner').classList.remove('hidden');

      const formData = new FormData(form);

      try {
        // Add to cart
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!addResponse.ok) throw new Error('Add to cart failed');

        // Get cart drawer and update it
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          // Reset drawer state if needed
          cartDrawer.classList.remove('is-empty');

          // Get sections to render
          const sections = cartDrawer.getSectionsToRender().map((section) => section.id);

          // Get cart data
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();

          // Only fetch sections if we have sections to render
          let sectionsData = {};
          if (sections && sections.length > 0) {
            // Use the current page URL as base
            const currentUrl = window.location.pathname;
            const sectionsResponse = await fetch(`${currentUrl}?sections=${sections.join(',')}`);
            sectionsData = await sectionsResponse.json();
          }

          // Update cart drawer
          cartDrawer.renderContents({
            items: cart.items,
            cart_level_discount_applications: cart.cart_level_discount_applications,
            total_price: cart.total_price,
            sections: sectionsData
          });

          // Reset drawer state and open
          requestAnimationFrame(() => {
            cartDrawer.classList.remove('is-empty');
            cartDrawer.open();
          });
        }

      } catch (error) {
        console.error('Error:', error);
      } finally {
        submitButton.classList.remove('loading');
        submitButton.querySelector('.loading__spinner').classList.add('hidden');
      }
    };

    form.addEventListener('submit', handleSubmit);
  }

  async handleFormSubmit(event) {
    if (event.target.id === 'subscription-form') {
      event.preventDefault();

      const submitButton = event.target.querySelector('button[type="submit"]');
      if (!submitButton) return;

      try {
        // Show loading state
        submitButton.disabled = true;
        submitButton.classList.add('loading');
        submitButton.querySelector('.loading__spinner')?.classList.remove('hidden');

        // Let the product-form component handle the submission
        const productForm = event.target.closest('product-form');
        if (productForm) {
          await productForm.submit();
        }
      } catch (error) {
        this.showError('Failed to add item to cart. Please try again.');
        console.error('Submission error:', error);
      } finally {
        // Reset loading state
        submitButton.disabled = false;
        submitButton.classList.remove('loading');
        submitButton.querySelector('.loading__spinner')?.classList.add('hidden');
      }
    }
  }

  bindStepNavigation() {
    // Bind step indicator clicks and keyboard interactions
    document.querySelectorAll('.step').forEach(step => {
      const handleStepActivation = () => {
        const stepNumber = parseInt(step.dataset.step);
        if (stepNumber <= this.maxStep) {
          this.goToStep(stepNumber);
        }
      };

      // Click handler
      step.addEventListener('click', handleStepActivation);

      // Keyboard handler
      step.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleStepActivation();
        }
      });
    });
  }

  goToStep(stepNumber) {
    if (stepNumber < 1 || stepNumber > 4) return;

    // No validation or reset when changing steps
    this.currentStep = stepNumber;
    this.maxStep = Math.max(this.maxStep, this.currentStep);
    this.updateSteps();
    this.renderCurrentStep();
    this.saveState();
  }

  validateStep() {
    switch (this.currentStep) {
      case 1:
        if (!this.selection.product) {
          this.showError('Please select a product to continue');
          return false;
        }
        break;
      case 2:
        if (!this.selection.quantity) {
          this.showError('Please select a quantity to continue');
          return false;
        }
        break;
      case 3:
        if (!this.selection.frequency) {
          this.showError('Please select a delivery frequency to continue');
          return false;
        }
        break;
    }
    return true;
  }

  showError(message) {
    const errorContainer = document.createElement('div');
    errorContainer.className = 'error-message';
    errorContainer.setAttribute('role', 'alert');
    errorContainer.setAttribute('aria-live', 'polite');
    errorContainer.textContent = message;
    errorContainer.style.cssText = `
      color: red;
      padding: 0.5rem;
      margin: 0.5rem 0;
      background: rgba(255, 0, 0, 0.1);
      border-radius: 4px;
    `;

    // Remove any existing error messages
    document.querySelectorAll('.error-message').forEach(el => el.remove());

    // Add new error message
    const activeStep = document.querySelector('.step-content.active');
    if (activeStep) {
      activeStep.insertAdjacentElement('afterbegin', errorContainer);
    }

    // Auto-remove after 3 seconds
    setTimeout(() => errorContainer.remove(), 3000);
  }

  nextStep() {
    if (this.currentStep < 4) {
      if (!this.validateStep()) return;
      this.currentStep++;
      this.maxStep = Math.max(this.maxStep, this.currentStep);
      this.updateSteps();
      this.saveState();
    }
  }

  updateSteps() {
    document.querySelectorAll('.step').forEach((step, index) => {
      const stepNum = index + 1;
      const isActive = stepNum === this.currentStep;
      const isSteppable = stepNum <= this.maxStep;
      const isCompleted = stepNum < this.currentStep;

      // Update classes
      step.classList.toggle('active', isActive);
      step.classList.toggle('steppable', isSteppable);
      step.classList.toggle('completed', isCompleted);

      // Update ARIA attributes
      step.setAttribute('aria-selected', isActive);
      step.setAttribute('aria-disabled', !isSteppable);
      step.setAttribute('tabindex', isSteppable ? '0' : '-1');

      // Update step selection text - Fixed selector to match new HTML structure
      const stepSelection = step.querySelector('.step-meta em');

      if (!stepSelection) {
        console.warn('Step selection element not found');
        return;
      }

      if (stepNum < 4) { // Skip Review step
        let selectionText = '';
        switch (stepNum) {
          case 1:
            selectionText = this.selection.product?.name || '';
            break;
          case 2:
            selectionText = this.selection.quantity || '';
            break;
          case 3:
            if (this.selection.frequency) {
              selectionText = `Every ${FREQUENCY_INTERVALS[this.selection.frequency]} week${FREQUENCY_INTERVALS[this.selection.frequency] > 1 ? 's' : ''}`;
            }
            break;
        }
        stepSelection.textContent = selectionText;
      } else {
        stepSelection.textContent = '';
      }
    });

    // Update content visibility
    document.querySelectorAll('.step-content').forEach((content, index) => {
      const isActive = index + 1 === this.currentStep;

      // Blur any focused element inside before hiding to prevent aria-hidden warning
      if (!isActive) {
        const focusedElement = content.querySelector(':focus');
        if (focusedElement) {
          focusedElement.blur();
        }
      }

      content.classList.toggle('active', isActive);
      content.setAttribute('aria-hidden', !isActive);
    });
  }

  renderCurrentStep() {
    // Hide all step contents first
    document.querySelectorAll('.step-content').forEach(content => {
      content.classList.remove('active');
    });

    // Show current step content
    const currentContent = document.querySelector(`#step-content-${this.currentStep}`);
    if (currentContent) {
      currentContent.classList.add('active');
    }

    switch (this.currentStep) {
      case 1:
        this.renderCategories();
        break;
      case 2:
        if (this.selection.product) {
          this.renderQuantities();
        }
        break;
      case 3:
        if (this.selection.quantity) {
          this.renderFrequencies();
        }
        break;
      case 4:
        if (this.selection.frequency) {
          this.renderSummary();
        }
        break;
    }
  }

}

// Initialize on DOM load
document.addEventListener('DOMContentLoaded', () => {
  new SubscriptionBuilder();
});

// No need to export since we're not using modules

// Helper function to update cart drawer
window.updateCartDrawer = async function () {
  try {
    const response = await fetch(window.Shopify.routes.root + 'cart.js');
    if (!response.ok) throw new Error('Network response was not ok');
    const cart = await response.json();

    // Dispatch cart update event
    window.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart }
    }));

    return cart;
  } catch (error) {
    console.error('Error updating cart:', error);
    throw error;
  }
};

// Helper function to open cart drawer
window.openCartDrawer = function () {
  // Dispatch event to open cart drawer
  window.dispatchEvent(new CustomEvent('cart:open'));
};
